import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { DashboardMetricsData } from '@/pages/dashboard/hooks/useDashboardData';
import { formatCurrency } from '@/pages/dashboard/useDashboardMetrics';
import { cx } from '@/utils/cx';

interface PipelineStagesViewProps {
  stages: DashboardMetricsData['pipelineStages'];
  currencyCode: string;
  orgId: number;
  pipelineName?: string;
}

/**
 * Small colored dot rendered next to a stage label. The color is
 * user-configured data (the stage's `ahoy_color`), so it can't be a static
 * Tailwind class — the inline `style` is isolated here per FRONTEND_PATTERNS.
 * Falls back to a neutral token dot when the stage has no color set.
 */
function StageColorDot({ color }: { color?: string }) {
  const style = useMemo(
    () => (color ? { backgroundColor: color } : undefined),
    [color]
  );
  return (
    <span
      aria-hidden='true'
      className={cx(
        'size-2 shrink-0 rounded-full',
        !color && 'bg-[var(--color-border-tertiary)]'
      )}
      style={style}
    />
  );
}

export function PipelineStagesView({
  stages,
  currencyCode,
  orgId,
  pipelineName,
}: PipelineStagesViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Whether there's more content to the right. Drives the right-edge fade so it
  // only shows when scrolling right would reveal more — not when the row fits or
  // is already scrolled to the end (the previous static gradient lingered there).
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollAffordance = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollRight(false);
      return;
    }
    // 1px tolerance for sub-pixel rounding at the scroll end.
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    // Recompute on mount, on scroll, and whenever the row resizes — covers
    // window resizes, sidebar toggles, and the stage list changing length.
    updateScrollAffordance();
    el.addEventListener('scroll', updateScrollAffordance, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollAffordance);
    resizeObserver.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollAffordance);
      resizeObserver.disconnect();
    };
  }, [updateScrollAffordance, stages]);

  if (stages.length === 0) {
    return null;
  }

  const pipelineParam = pipelineName
    ? `&pipeline=${encodeURIComponent(pipelineName)}`
    : '';

  return (
    <div className='relative'>
      {canScrollRight && (
        <div
          aria-hidden='true'
          className='pointer-events-none absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-primary to-transparent z-10'
        />
      )}
      <div
        ref={scrollRef}
        aria-label='Pipeline stages'
        className='flex gap-4 overflow-x-auto pb-3 snap-x'
        role='region'
      >
        {stages.map(stage => (
          <Link
            key={stage.stageEntityId}
            aria-label={`${stage.stageLabel}: ${stage.dealCount} ${stage.dealCount === 1 ? 'deal' : 'deals'}, ${formatCurrency(stage.totalValue, currencyCode)} total value`}
            className='shrink-0 w-[180px] snap-start bg-bg-primary border border-secondary rounded-[4px] p-4 hover:border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)]'
            to={`/${orgId}/crm/deals?stageId=${encodeURIComponent(stage.stageEntityId)}${pipelineParam}`}
          >
            <div className='flex items-center gap-1.5'>
              <StageColorDot color={stage.stageColor} />
              <span className='text-[13px] font-semibold text-tertiary truncate'>
                {stage.stageLabel}
              </span>
            </div>
            <div className='text-[18px] font-semibold text-fg-primary'>
              {formatCurrency(stage.totalValue, currencyCode)}
            </div>
            <div className='text-[12px] font-medium text-tertiary'>
              {stage.dealCount} {stage.dealCount === 1 ? 'deal' : 'deals'}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
